import { Card, CardContent } from '@/components/ui/card';
import { RatingStars } from '@/components/host/ratings/RatingStars';
import { Star, ThumbsUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

interface PlaceRatingsProps {
  placeId: string;
  rating?: number;
  ratingCount?: number;
  googleRating?: number;
  googleReviewsCount?: number;
  isLoggedIn: boolean;
}

export function PlaceRatings({
  placeId,
  rating,
  ratingCount,
  googleRating,
  googleReviewsCount,
  isLoggedIn,
}: PlaceRatingsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch aggregate rating for the place
  const { data: aggregateRating } = useQuery({
    queryKey: ['place-aggregate-rating', placeId],
    queryFn: async () => {
      console.log('Fetching aggregate rating for place:', placeId);
      
      const { data, error } = await supabase
        .from('destination_playlist_items')
        .select('rating, rating_count')
        .eq('id', placeId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching aggregate rating:', error);
        throw error;
      }
      console.log('Aggregate rating data:', data);
      return data || { rating: 0, rating_count: 0 };
    }
  });

  // Fetch user's existing rating
  const { data: userRating } = useQuery({
    queryKey: ['place-rating', placeId],
    enabled: isLoggedIn,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      console.log('Fetching rating for user:', user.id, 'place:', placeId);
      
      const { data, error } = await supabase
        .from('place_ratings')
        .select('rating')
        .eq('place_id', placeId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching rating:', error);
        throw error;
      }
      console.log('User rating data:', data);
      return data?.rating || 0;
    }
  });

  const rateMutation = useMutation({
    mutationFn: async (newRating: number) => {
      if (!isLoggedIn) {
        throw new Error('Must be logged in to rate');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      console.log('Rating mutation - User:', user.id, 'Place:', placeId, 'Rating:', newRating);

      const { error: upsertError } = await supabase
        .from('place_ratings')
        .upsert(
          {
            place_id: placeId,
            user_id: user.id,
            rating: newRating
          },
          {
            onConflict: 'place_id,user_id'
          }
        );

      if (upsertError) {
        console.error('Rating error:', upsertError);
        throw upsertError;
      }

      // Return the new rating for optimistic updates
      return newRating;
    },
    onMutate: async (newRating) => {
      await queryClient.cancelQueries({ queryKey: ['place-rating', placeId] });
      await queryClient.cancelQueries({ queryKey: ['place-aggregate-rating', placeId] });

      const previousRating = queryClient.getQueryData(['place-rating', placeId]);
      queryClient.setQueryData(['place-rating', placeId], newRating);

      return { previousRating };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['place-rating', placeId] });
      queryClient.invalidateQueries({ queryKey: ['place-aggregate-rating', placeId] });
      queryClient.invalidateQueries({ queryKey: ['destination_playlist_items'] });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Rating updated successfully",
      });
    },
    onError: (error: Error, _, context) => {
      if (context?.previousRating) {
        queryClient.setQueryData(['place-rating', placeId], context.previousRating);
      }

      if (error.message === 'Must be logged in to rate') {
        navigate('/auth');
        toast({
          title: "Authentication Required",
          description: "Please sign in to rate this place",
          variant: "destructive",
        });
      } else {
        console.error('Rating error:', error);
        toast({
          title: "Error",
          description: "Failed to update rating",
          variant: "destructive",
        });
      }
    }
  });

  return (
    <Card>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-purple-600" />
              Travilis Ratings
            </h3>
            <div className="flex items-center gap-2">
              <RatingStars rating={aggregateRating?.rating || 0} size="lg" />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                ({aggregateRating?.rating_count || 0} {aggregateRating?.rating_count === 1 ? 'rating' : 'ratings'})
              </span>
            </div>

            {isLoggedIn ? (
              <div className="space-y-4 pt-4">
                <h4 className="font-medium">Your Rating</h4>
                <RatingStars
                  rating={userRating || 0}
                  size="md"
                  interactive
                  onRatingChange={(rating) => rateMutation.mutate(rating)}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Sign in to rate this place
              </p>
            )}
          </div>

          {(googleRating || googleReviewsCount) && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <img 
                  src="https://www.google.com/favicon.ico" 
                  alt="Google" 
                  className="w-5 h-5" 
                />
                <span>Rating</span>
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="ml-1 font-semibold">{googleRating?.toFixed(1)}</span>
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  ({googleReviewsCount} {googleReviewsCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
